import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { Grid } from "../../../../../Common/Grid"
import { TitleHeader } from "../../../../Common/TitleHeader"
import { useNotification } from "../../../../../../../hooks"
import { generatePath, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Box, CircularProgress, Typography } from "@mui/material"
import { Button } from "@fullscreendigital/carrefour-dex-fe"
import { makeCoreGetFileRequest } from "../../../../../../../utils/api"
import { formatErrorMessage } from "../../../../../../../utils/formatting"
import { useQuery } from "react-query"
import { URLS } from "../../../../constants"

import { pdfjs, Document, Page } from "react-pdf"
import { PrintPreviewModal } from "../../../../../../waste-management/common/PrintPreviewModal"
pdfjs.GlobalWorkerOptions.workerSrc = "static/pdfWorker/pdf.worker.min.js"

const ReceiptReport = () => {
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const { id: orderId } = useParams<{ id: string }>()
  const [sp] = useSearchParams()
  const supplierCode = sp.get("supplierCode")

  const [isPrintOpen, setIsPrintOpen] = useState(false)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [pageWidth, setPageWidth] = useState<number>(0)
  const [zoom, setZoom] = useState(1)

  const zoomIn = () => setZoom(z => Math.min(2.0, Number((z + 0.1).toFixed(1))))
  const zoomOut = () => setZoom(z => Math.max(0.6, Number((z - 0.1).toFixed(1))))
  const resetZoom = () => setZoom(1)

  const updateWidth = () => {
    const el = containerRef.current
    if (!el) return
    const w = el.clientWidth || el.getBoundingClientRect().width || 0
    setPageWidth(Math.max(0, Math.floor(w - 16)))
  }

  useLayoutEffect(() => {
    updateWidth()
    requestAnimationFrame(updateWidth)

    const el = containerRef.current
    if (!el) return

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth)
      return () => window.removeEventListener("resize", updateWidth)
    }

    const ro = new ResizeObserver(() => updateWidth())
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  async function getReceiptReportPdf(refId: string | number): Promise<Blob> {
    const url = `/shop-reception/ds-orders/report-print/${encodeURIComponent(String(refId))}`

    const res: any = await makeCoreGetFileRequest(url, { responseType: "arraybuffer" } as any)
    const data = res?.data ?? res
    return new Blob([data], { type: "application/pdf" })
  }

  const { data: pdfBlob, isLoading, isError, refetch } = useQuery(
    ["receiptReportPdf", orderId],
    () => getReceiptReportPdf(orderId as string),
    {
      enabled: !!orderId,
      onError: (e: any) => showNotification({ message: formatErrorMessage(e), type: "error" }),
      retry: 1,
    }
  )

  const pdfUrl = useMemo(() => {
    if (!pdfBlob) return null
    return URL.createObjectURL(pdfBlob)
  }, [pdfBlob])

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
    }
  }, [pdfUrl])

  const handleDownload = () => {
    if (!pdfUrl) return
    const a = document.createElement("a")
    a.href = pdfUrl
    a.download = `raport_receptie_${String(orderId)}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  const handlePrint = () => {
    setIsPrintOpen(true)
  }

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setPageNumber(1)
    requestAnimationFrame(updateWidth)
  }

  const onDocumentLoadError = (error: any) => {
    if (error?.message === "Worker was destroyed") return
    showNotification({
      message: `A aparut o eroare la afisarea fisierului. ${error.message}`,
      type: "error",
    })
  }

  const goToPrevPage = () => setPageNumber((p) => Math.max(p - 1, 1))
  const goToNextPage = () => setPageNumber((p) => Math.min(p + 1, numPages ?? p))

  if (isLoading) return <div style={{ padding: 16 }}>Loading PDF…</div>

  if (isError)
    return (
      <div style={{ padding: 16 }}>
        <TitleHeader title="RAPORT RECEPTIE" />

        <Typography variant="h6" style={{ marginTop: 24 }}>
          Could not load receipt report PDF
        </Typography>

        <div style={{ marginTop: 24 }}>
          <Button type="button" variant="contained" label="Retry" onClick={() => refetch()} style={{ width: "100%" }} />
        </div>
      </div>
    )

  if (!pdfBlob) return <div style={{ padding: 16 }}>No PDF.</div>

  const items = [
    <TitleHeader title="RAPORT RECEPTIE" />,
    <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <Typography variant="h6">Raport de Receptie</Typography>

        <div style={{ display: "flex", gap: 8 }}>
          <Button type="button" variant="contained" label="Printeaza" onClick={handlePrint} style={{ width: "100%" }} />
          <Button type="button" variant="contained" label="Descarca" onClick={handleDownload} style={{ width: "100%" }} />
        </div>
      </div>

      <div ref={containerRef} style={{ border: "1px solid #ddd", borderRadius: 8, overflow: "auto", background: "#fff", padding: 8 }}>
        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Button type="button" variant="outlined" label="<" onClick={goToPrevPage} disabled={pageNumber <= 1} />
          <Typography style={{ fontWeight: 600 }}>
            Pagina {pageNumber}
            {numPages ? ` / ${numPages}` : ""}
          </Typography>
          <Button type="button" variant="outlined" label=">" onClick={goToNextPage} disabled={numPages ? pageNumber >= numPages : true} />
        </Box>

        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Button variant="outlined" label="-" onClick={zoomOut} />
          <Button variant="outlined" label={`${Math.round(zoom * 100)}%`} onClick={resetZoom} />
          <Button variant="outlined" label="+" onClick={zoomIn} />
        </Box>

        <Document
          file={pdfBlob}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={<CircularProgress color="primary" style={{ margin: "40px auto", display: "block" }} />}
        >
          <Page
            key={`${pageNumber}-${pageWidth}-${zoom}`}
            pageNumber={pageNumber}
            width={pageWidth || undefined}
            scale={zoom}
            loading={<CircularProgress color="primary" style={{ margin: "40px auto", display: "block" }} />}
            renderAnnotationLayer={false}
            renderTextLayer={false}
          />
        </Document>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <Button
          type="button"
          variant="contained"
          label="Continua catre incarcarea avizului"
          onClick={() =>
            navigate(generatePath(URLS.AUTH_SUPPLIERS_NOTICE_INPUT, { id: orderId }) + `?supplierCode=${supplierCode}`)
          }
          style={{ width: "100%" }}
        />
      </div>

      <PrintPreviewModal
        isModalOpen={isPrintOpen}
        closeModal={() => setIsPrintOpen(false)}
        pdfBlob={pdfBlob}
        isPdfBlobLoading={false}
        printScale={1.5}
      />
    </div>,
  ].filter(Boolean)

  return <Grid gridContainerStyle={{ marginTop: "20px" }} itemsPerRow={1} items={items} />
}

export default ReceiptReport
